import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

type GalleryImage = { id: string; src: string; alt: string }

const GAS_URL = process.env.REACT_APP_GAS_GALLERY_URL || ''
const ZM_IMAGES_JSON = '/zm/images.json'

const FALLBACK: GalleryImage[] = [
  { id: 'f1', src: '/images/homematsproductcards/MARSHALL SCRIBE CUSTOM LO.webp', alt: 'Marshall Scribe Mat' },
  { id: 'f2', src: '/images/homematsproductcards/NOVAMAT CUSTOM LOGO MAT.webp', alt: 'Novamat Logo Mat' },
  { id: 'f3', src: '/images/homematsproductcards/SUPER DECO CUSTOM LOGO MAT.webp', alt: 'Super Deco Mat' },
  { id: 'f4', src: '/images/homematsproductcards/NOVAMAT CUSTOM LOGO MAT2.webp', alt: 'Novamat Mat 2' },
  { id: 'f5', src: '/images/homematsproductcards/SUPER DECO CUSTOM LOGO (2).webp', alt: 'Super Deco Mat 2' },
  { id: 'f6', src: '/images/homematsproductcards/SUPER DECO CUSTOM LOGO (3).webp', alt: 'Super Deco Mat 3' },
  { id: 'f7', src: '/images/homematsproductcards/SUPER DECO CUSTOM LOGO (4).webp', alt: 'Super Deco Mat 4' },
]

export default function HomeGallerySection() {
  const [images, setImages] = useState<GalleryImage[]>(FALLBACK)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [loadedIds, setLoadedIds] = useState<Set<string>>(() => new Set())
  const markLoaded = (id: string) => setLoadedIds((prev) => new Set(prev).add(id))

  const lightboxImg = lightboxIndex !== null ? images[lightboxIndex] : null
  const goPrev = () => setLightboxIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length))
  const goNext = () => setLightboxIndex((i) => (i === null ? null : (i + 1) % images.length))

  useEffect(() => {
    if (lightboxIndex === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null)
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex])
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Prefer local public/zm (same origin, indexable)
    fetch(ZM_IMAGES_JSON, { redirect: 'follow' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: { sections?: { images: GalleryImage[] }[] }) => {
        const all = data.sections?.flatMap((s) => s.images) ?? []
        if (all.length > 0) {
          setImages(all.slice(0, 12))
          setLoadedIds(new Set())
          return
        }
        throw new Error()
      })
      .catch(() => {
        if (!GAS_URL) return
        fetch(GAS_URL, { redirect: 'follow' })
          .then((r) => r.json())
          .then((data: { sections?: { images: GalleryImage[] }[] }) => {
            const all = data.sections?.flatMap((s) => s.images) ?? []
            if (all.length > 0) {
              setImages(all.slice(0, 12))
              setLoadedIds(new Set())
            }
          })
          .catch(() => {})
      })
  }, [])

  return (
    <section className="w-full bg-white py-14 sm:py-20">
      <div className="px-4 sm:px-8 lg:px-12">
        {/* Scroll row */}
        <div className="relative">

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {/* Intro card — always first */}
            <div
              className="flex-shrink-0 flex flex-col justify-between rounded-2xl p-7 sm:p-8"
              style={{
                width: 'clamp(230px, 28vw, 320px)',
                height: '374px',
                background: 'linear-gradient(145deg, #ec4899 0%, #be185d 100%)',
                scrollSnapAlign: 'start',
              }}
            >
              <div>
                <p className="text-xs font-semibold text-pink-100 uppercase tracking-widest mb-3">Our Work</p>
                <h2 className="text-2xl sm:text-3xl font-bold text-white leading-snug">
                  Premium branded door mats, crafted for your space.
                </h2>
              </div>
              <Link
                to="/gallery"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/40 text-white text-sm font-medium px-5 py-2.5 hover:bg-white/10 transition-colors w-fit"
              >
                View all photos
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
            </div>

            {/* Image cards */}
            {images.map((img, i) => {
              const isLoaded = loadedIds.has(img.id)
              return (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className="flex-shrink-0 relative rounded-2xl overflow-hidden group cursor-pointer text-left bg-gray-200"
                  style={{
                    width: 'clamp(242px, 29vw, 330px)',
                    height: '374px',
                    scrollSnapAlign: 'start',
                  }}
                >
                  {/* Low-fi placeholder while loading */}
                  {!isLoaded && (
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6"
                      style={{ height: '100%' }}
                    >
                      <div className="w-16 h-16 rounded-xl bg-gray-300 animate-pulse" />
                      <div className="h-3 w-3/4 max-w-[180px] rounded bg-gray-300 animate-pulse" />
                      <div className="h-3 w-1/2 max-w-[120px] rounded bg-gray-300 animate-pulse" />
                    </div>
                  )}
                  <img
                    src={img.src}
                    alt={img.alt}
                    onLoad={() => markLoaded(img.id)}
                    onError={() => markLoaded(img.id)}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                  loading="lazy"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5 pointer-events-none">
                    <p className="text-white font-semibold text-sm leading-snug">{img.alt}</p>
                  </div>
                </button>
              )
            })}
          </div>

        </div>
      </div>

      {/* Lightbox — full page, above header & floating buttons */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            className="absolute top-4 right-4 z-[10000] w-12 h-12 rounded-full bg-white/90 hover:bg-white text-gray-900 flex items-center justify-center transition-colors shadow-lg"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-[10000] w-14 h-14 rounded-full bg-white/90 hover:bg-white text-gray-900 flex items-center justify-center transition-colors shadow-lg"
            aria-label="Previous image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-[10000] w-14 h-14 rounded-full bg-white/90 hover:bg-white text-gray-900 flex items-center justify-center transition-colors shadow-lg"
            aria-label="Next image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <img
            src={encodeURI(lightboxImg.src)}
            alt={lightboxImg.alt}
            className="max-w-[100vw] max-h-[100vh] w-auto h-auto object-contain"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
        </div>
      )}
    </section>
  )
}
